<template>
  <div class="max-w-7xl mx-auto mt-8 bg-white p-6 rounded-xl shadow-md">
    <!-- Header -->
    <div class="mb-6">
      <h2 class="text-2xl font-bold text-gray-800 mb-2">
        Benachrichtigungen aktivieren
      </h2>
      <p class="text-gray-600">
        Folge diesen Schritten, um sicherzustellen, dass du alle Benachrichtigungen korrekt empfängst.
      </p>
    </div>

    <div class="space-y-6">

      <!-- Schritt 1: Nicht stören / Fokus-Modus -->
      <section>
        <button
          class="w-full flex justify-between items-center text-left"
          @click="toggle('focus')"
        >
          <h3 class="text-xl font-semibold text-gray-800">1. Nicht stören / Fokus-Modus ausschalten</h3>
          <ChevronDownIcon v-if="!open.focus" class="h-6 w-6 text-gray-600"/>
          <ChevronUpIcon v-else class="h-6 w-6 text-gray-600"/>
        </button>

        <div v-if="open.focus" class="mt-3 text-gray-700 space-y-4">
          <p>Überprüfe, ob auf deinem Gerät der <strong>Nicht stören</strong>-Modus oder der <strong>Fokus-Modus</strong> aktiviert ist. Schalte ihn aus, damit Benachrichtigungen angezeigt werden können:</p>

          <!-- Windows -->
          <section class="border rounded-lg p-4">
            <button class="w-full flex justify-between items-center text-left" @click="toggle('focusWindows')">
              <h4 class="text-lg font-semibold text-gray-800">Windows</h4>
              <ChevronDownIcon v-if="!open.focusWindows" class="h-5 w-5 text-gray-600"/>
              <ChevronUpIcon v-else class="h-5 w-5 text-gray-600"/>
            </button>
            <div v-if="open.focusWindows" class="mt-3">
              <ol class="list-decimal list-inside space-y-2">
                <li>Öffne das <strong>Startmenü</strong>.</li>
                <li>Gehe zu <strong>Einstellungen → System → Benachrichtigungen</strong>.</li>
                <li>Schalte <strong>Bitte nicht stören</strong> aus.</li>
              </ol>
            </div>  
          </section>

          <!-- macOS -->
          <section class="border rounded-lg p-4">
            <button class="w-full flex justify-between items-center text-left" @click="toggle('focusMac')">
              <h4 class="text-lg font-semibold text-gray-800">macOS</h4>
              <ChevronDownIcon v-if="!open.focusMac" class="h-5 w-5 text-gray-600"/>
              <ChevronUpIcon v-else class="h-5 w-5 text-gray-600"/>
            </button>
            <div v-if="open.focusMac" class="mt-3">
              <ol class="list-decimal list-inside space-y-2">
                <li>Öffne das <strong>Kontrollzentrum</strong> <img class="inline-block align-text-bottom ml-1" 
                      src="https://www.heise.de/select/mac-and-i/2020/5/1602464259647410/contentimages/Icon_Mac_ControlCenter.jpg" 
                      alt="Chrome Regler-Symbol" 
                      style="height: 1.5em;"> oben in der Menüleiste.</li>
                <li>Klicke auf <strong>Fokus</strong>.</li>
                <li>Überprüfe, ob ein Fokus-Modus (z. B. „Nicht stören“) aktiviert ist.</li>
                <li>Deaktiviere den Fokus-Modus, damit Benachrichtigungen angezeigt werden.</li>
              </ol>
            </div>
          </section>
        </div>
      </section>

      <!-- Schritt 2: OS-spezifische Benachrichtigungen -->
      <section>
        <button
          class="w-full flex justify-between items-center text-left"
          @click="toggle('os')"
        >
          <h3 class="text-xl font-semibold text-gray-800">2. Betriebssystem-Benachrichtigungen aktivieren</h3>
          <ChevronDownIcon v-if="!open.os" class="h-6 w-6 text-gray-600"/>
          <ChevronUpIcon v-else class="h-6 w-6 text-gray-600"/>
        </button>

        <div v-if="open.os" class="mt-4 space-y-6">

          <!-- Windows -->
          <section class="border rounded-lg p-4">
            <button class="w-full flex justify-between items-center text-left"
                    @click="toggle('windows')">
              <h4 class="text-lg font-semibold text-gray-800">Windows</h4>
              <ChevronDownIcon v-if="!open.windows" class="h-5 w-5 text-gray-600"/>
              <ChevronUpIcon v-else class="h-5 w-5 text-gray-600"/>
            </button>
            <div v-if="open.windows" class="mt-3 text-gray-700">
              <ol class="list-decimal list-inside space-y-2">
                <li>Öffne das <strong>Startmenü</strong>.</li>
                <li>Gehe zu <strong>Einstellungen → System → Benachrichtigungen</strong>.</li>
                <li>Stelle <strong>Benachrichtigungen</strong> ein</li>
                <li>Wähle deinen Browser aus.</li>
                <li>Aktiviere <strong>Benachrichtigungen zulassen</strong>.</li>
                <li>Checke sicherheitshalber, ob es mehrere Einträge für deinen Browser in der Liste gibt</li>
              </ol>
            </div>
          </section>

          <!-- macOS -->
          <section class="border rounded-lg p-4">
            <button class="w-full flex justify-between items-center text-left"
                    @click="toggle('mac')">
              <h4 class="text-lg font-semibold text-gray-800">macOS</h4>
              <ChevronDownIcon v-if="!open.mac" class="h-5 w-5 text-gray-600"/>
              <ChevronUpIcon v-else class="h-5 w-5 text-gray-600"/>
            </button>
            <div v-if="open.mac" class="mt-3 text-gray-700">
              <ol class="list-decimal list-inside space-y-2">
                <li>Öffne <strong>Apple-Menü → Systemeinstellungen → Mitteilungen</strong>.</li>
                <li>Wähle deinen Browser aus der Liste aus.</li>
                <li>Aktiviere <strong>Mitteilungen erlauben</strong>.</li>
                <li>Stelle sicher, dass Banner oder Hinweise aktiviert sind.</li>
                <li>Checke sicherheitshalber, ob es mehrere Einträge für deinen Browser in der Liste gibt</li>
              </ol>
            </div>
          </section>
        </div>
      </section>

      <!-- Schritt 3: Browser-spezifische Berechtigungen -->
      <section>
        <button
          class="w-full flex justify-between items-center text-left"
          @click="toggle('browser')"
        >
          <h3 class="text-xl font-semibold text-gray-800">3. Browser-Berechtigungen aktivieren</h3>
          <ChevronDownIcon v-if="!open.browser" class="h-6 w-6 text-gray-600"/>
          <ChevronUpIcon v-else class="h-6 w-6 text-gray-600"/>
        </button>

        <div v-if="open.browser" class="mt-3 text-gray-700 space-y-6">

          <!-- Chrome -->
          <section class="border rounded-lg p-4">
            <button class="w-full flex justify-between items-center text-left"
                    @click="toggle('chrome')">
              <h4 class="text-lg font-semibold text-gray-800">Google Chrome</h4>
              <ChevronDownIcon v-if="!open.chrome" class="h-5 w-5 text-gray-600"/>
              <ChevronUpIcon v-else class="h-5 w-5 text-gray-600"/>
            </button>
            <div v-if="open.chrome" class="mt-3">
              <ol class="list-decimal list-inside space-y-2">
                <li>
                  Klicke in der Adressleiste auf das <strong>Regler-Symbol</strong>
                  <img class="inline-block align-text-bottom ml-1" 
                      src="https://scr.wfcdn.de/3441/Chrome-1683197099-0-0.jpg?width=420&height=420" 
                      alt="Chrome Regler-Symbol" 
                      style="height: 1.5em;">
                </li>
                <li>Wähle <strong>Website-Einstellungen</strong>.</li>
                <li>Scrolle zu <strong>Benachrichtigungen</strong>.</li>
                <li>Setze auf <strong>Zulassen</strong>.</li>
                <li>Seite neu laden.</li>
              </ol>
            </div>
          </section>

          <!-- Firefox -->
          <section class="border rounded-lg p-4">
            <button class="w-full flex justify-between items-center text-left"
                    @click="toggle('firefox')">
              <h4 class="text-lg font-semibold text-gray-800">Mozilla Firefox</h4>
              <ChevronDownIcon v-if="!open.firefox" class="h-5 w-5 text-gray-600"/>
              <ChevronUpIcon v-else class="h-5 w-5 text-gray-600"/>
            </button>
            <div v-if="open.firefox" class="mt-3">
              <ol class="list-decimal list-inside space-y-2">
                <li>Navigiere auf dieser Webapp zu der Feedbackseite</li>
                <li>Klicke auf das <strong>Regler-Symbol</strong>.
                <img class="inline-block align-text-bottom ml-1" 
                      src="https://scr.wfcdn.de/3441/Chrome-1683197099-0-0.jpg?width=420&height=420" 
                      alt="Firefox Regler-Symbol" 
                      style="height: 1.5em;">
                </li>
                <li>Wähle <strong>Berechtigungen</strong>.</li>
                <li>Setze <strong>Benachrichtigungen senden</strong> auf <strong>Erlaubt</strong>.</li>
              </ol>
            </div>
          </section>

          <!-- Safari -->
          <section class="border rounded-lg p-4">
            <button class="w-full flex justify-between items-center text-left"
                    @click="toggle('safari')">
              <h4 class="text-lg font-semibold text-gray-800">Apple Safari</h4>
              <ChevronDownIcon v-if="!open.safari" class="h-5 w-5 text-gray-600"/>
              <ChevronUpIcon v-else class="h-5 w-5 text-gray-600"/>
            </button>
            <div v-if="open.safari" class="mt-3">
              <ol class="list-decimal list-inside space-y-2">
                <li>Safari öffnen → Einstellungen → Websites → Mitteilungen.</li>
                <li>Die Website auswählen → <strong>Zulassen</strong>.</li>
              </ol>
            </div>
          </section>

        </div>
      </section>

    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/24/solid";

const open = ref({
  focus: false,
  os: false,
  windows: false,
  mac: false,
  browser: false,
  chrome: false,
  firefox: false,
  safari: false
});

const toggle = (key) => {
  open.value[key] = !open.value[key];
};
</script>
